import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import EditorInput from '~/components/EditorInput'
import Icon from '~/components/Icon'
import { sanitizeHtml } from '~/utils/sanitizeHtml'

const EditorInputPage: Component = () => {
  const [content, setContent] = createSignal('<h1>Edición Premium</h1><p>Escribe contenido <strong>enriquecido</strong> con total libertad.</p>')

  return (
    <>
      <Title>Editor Input - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">EditorInput</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Editor de texto enriquecido (WYSIWYG) de alto rendimiento. Permite la creación de contenido HTML complejo mediante una interfaz intuitiva con toolbar dinámico, soporte para multimedia y
            modo pantalla completa.
          </p>
        </div>

        <div class="space-y-12">
          {/* Experiencia de Edición Standard */}
          <Card>
            <Card.Header
              title="Entorno de Redacción"
              subtitle="Toolbar completo con soporte para formatos de texto, listas, citas y enlaces."
              icon={<Icon icon="material-symbols-light:edit-square-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="p-8 bg-surface-muted rounded-xl border border-border">
                <EditorInput value={content()} onChange={setContent} placeholder="Escribe tu próximo gran artículo..." class="bg-surface rounded-xl shadow-lg border-2 border-border" />
                <div class="mt-6 flex justify-end gap-3 font-mono text-[10px] text-foreground-subtle italic">
                  <span>Caracteres: {content().length}</span>
                  <span>Modificado: Justo ahora</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Versatilidad de Dimensiones */}
          <Card>
            <Card.Header
              title="Adaptabilidad Visual"
              subtitle="Configura alturas mínimas y máximas para diferentes contextos, desde comentarios breves hasta artículos extensos."
              icon={<Icon icon="material-symbols-light:maximize" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="space-y-4">
                  <Badge color="info" variant="soft">
                    Compact Mode (Comentarios)
                  </Badge>
                  <EditorInput minHeight="min-h-[100px]" maxHeight="200px" placeholder="Escribe un comentario..." />
                </div>
                <div class="space-y-4">
                  <Badge color="secondary" variant="soft">
                    Disabled View (Solo Lectura)
                  </Badge>
                  <EditorInput value="<p>Este contenido no puede ser editado por el usuario actual.</p>" disabled />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Preview y Sanitización */}
          <Card>
            <Card.Header
              title="Seguridad y Renderizado"
              subtitle="Visualiza el resultado final mientras el sistema protege la aplicación contra inyecciones maliciosas."
              icon={<Icon icon="material-symbols-light:verified-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
                <div class="flex flex-col gap-4">
                  <h4 class="text-xs font-semibold text-foreground-subtle pl-3 border-l-4 border-primary">Live Preview</h4>
                  <div class="flex-1 p-8 rounded-2xl bg-surface border border-border shadow-inner overflow-hidden prose max-w-none" innerHTML={sanitizeHtml(content())} />
                </div>

                <div class="p-8 bg-slate-900 rounded-2xl flex flex-col justify-center text-white relative overflow-hidden ring-1 ring-white/10">
                  <Icon icon="material-symbols-light:code" class="absolute -right-6 -top-6 opacity-10 text-white" size={140} />
                  <div class="relative z-10 space-y-4">
                    <Badge color="success">Auto-Sanitize Active</Badge>
                    <h3 class="text-xl font-black italic tracking-tighter uppercase">XSS Protection Layer</h3>
                    <p class="text-foreground-subtle text-sm leading-relaxed">
                      El editor remueve automáticamente etiquetas <code class="text-success-400">{`<script>`}</code>, atributos inline de JS y estilos en línea que podrían comprometer la seguridad de
                      tu plataforma.
                    </p>
                    <div class="bg-black/40 p-4 rounded-lg font-mono text-[10px] text-emerald-400 border border-white/5">{`// Input: <script>alert(1)</script>\n// Output: [ELIMINADO]`}</div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Configuración técnica para el componente EditorInput."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">value</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Contenido HTML crudo para editar.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onChange</td>
                      <td class="px-6 py-4 italic text-xs">(html: string) =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Callback con el HTML resultante del editor.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">minHeight / maxHeight</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic text-balance">Límites de scroll vertical (ej: '300px').</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">placeholder</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Texto sugerido para contenedor vacío.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">disabled</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Desactiva la toolbar y la edición.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default EditorInputPage
